import ApartmentIcon from '@mui/icons-material/Apartment';
import { Box, Paper, SvgIcon } from '@mui/material';
import Typography from '@mui/material/Typography';
import emptySensorImage from 'assets/images/sensor1.png';
import ConnectionGif from 'assets/images/icons/connection_slow.gif';
import MDTypography from 'components/MDTypography/index';
import PingPongText from 'components/PingPongText/PingPongText';
import SmallStatusLabel from 'components/SmallStatusLabel/SmallStatusLabel';
import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { brandColors } from 'types/colors';
import { Status, StatusColor, StatusIcon } from 'types/status';

const PARAM_BOX_HEIGHT = { xs: '3.5rem', sm: '4rem' };
const INACTIVE_TIME_SECONDS = 900;
const SMALL_BOX_LABEL_SX = {
  fontSize: { xs: 14, sm: 14 },
  fontWeight: 600,
  color: '#000',
  overflowWrap: 'anywhere',
  lineHeight: 1.25,
  textAlign: 'center',
};
const SMALL_BOX_VALUE_SX = {
  fontSize: { xs: 22, sm: 22 },
  overflowWrap: 'anywhere',
  textAlign: 'end',
  fontWeight: 700,
};
const GREEN_PARAM_VALUE_SX = { ...SMALL_BOX_VALUE_SX, color: '#00A551' };
const YELLOW_PARAM_VALUE_SX = { ...SMALL_BOX_VALUE_SX, color: '#FCB400' };
const RED_PARAM_VALUE_SX = { ...SMALL_BOX_VALUE_SX, color: '#BE1E2D' };
const GRAY_PARAM_VALUE_SX = { ...SMALL_BOX_VALUE_SX, color: '#4e4e4e' };
const BDI_PARAM_VALUE_SX = { ...SMALL_BOX_VALUE_SX, color: '#000000cc', fontWeight: 600 };
const BDI_PARAM_BOX_COLORS = {
  'Thermal Justified': brandColors.BLUE_LIGHT,
  'Air Justified': brandColors.BLUE_LIGHT,
  'Thermal Saveable': brandColors.GREEN_LIGHT,
  'Air Saveable': brandColors.GREEN_LIGHT,
  'Thermal Requested': brandColors.RED_LIGHT,
  'Air Requested': brandColors.RED_LIGHT,
};
const BDI_PARAM_LABEL_COLORS = {
  'Thermal Justified': brandColors.BLUE,
  'Air Justified': brandColors.BLUE,
  'Thermal Saveable': brandColors.GREEN,
  'Air Saveable': brandColors.GREEN,
  'Thermal Requested': brandColors.RED,
  'Air Requested': brandColors.RED,
};
const PARAM_BOX_SX = {
  aspectRatio: 'auto',
  pt: 1,
  mb: 1,
  borderRadius: '16px',
  display: 'flex',
  width: '5.7rem',
  height: PARAM_BOX_HEIGHT,
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
};

const getParamValueSx = value => {
  if (value >= 85) {
    return GREEN_PARAM_VALUE_SX;
  }
  if (value <= 84 && value >= 71) {
    return YELLOW_PARAM_VALUE_SX;
  }
  if (value <= 70) {
    return RED_PARAM_VALUE_SX;
  }
  if (value === 0) {
    return GRAY_PARAM_VALUE_SX;
  }
  return SMALL_BOX_VALUE_SX;
};

function SensorImageFrame({ children, sx, backgroundColor }) {
  return (
    <Box
      sx={{
        position: 'relative',
        // width: 100,
        aspectRatio: '1 / 1',
        overflow: 'hidden',
        borderRadius: 2,
        background: `linear-gradient(to bottom, ${backgroundColor} 0%, transparent 100%)`,

        ...sx,
      }}>
      {children}
    </Box>
  );
}

const SensorParameterBox = memo(function SensorParameterBox({ label, value }) {
  const backgroundColor =
    BDI_PARAM_BOX_COLORS[label] ||
    (value >= 85 ? brandColors.GREEN_LIGHT : value >= 71 ? brandColors.YELLOW_LIGHT : brandColors.RED_LIGHT);
  const isBdiParam = Boolean(BDI_PARAM_BOX_COLORS[label]);
  const labelColor = BDI_PARAM_LABEL_COLORS[label] || SMALL_BOX_LABEL_SX.color;
  const valueSx = isBdiParam ? BDI_PARAM_VALUE_SX : getParamValueSx(value);

  return (
    <SensorImageFrame className="param-box" backgroundColor={backgroundColor} sx={PARAM_BOX_SX}>
      <MDTypography sx={{ ...SMALL_BOX_LABEL_SX, color: labelColor, ...(isBdiParam && { fontSize: '.8rem' }) }}>
        {label}
      </MDTypography>
      <Typography sx={valueSx}>{value === -1 ? 'N/A' : `${value}%`}</Typography>
    </SensorImageFrame>
  );
});

export const SensorCard = (props = {}) => {
  const navigate = useNavigate();
  const isManageView = props.manage === true;
  const status = props.status || Status.Disconnected;

  const now = Math.floor(Date.now() / 1000);
  const lastRecordTime = Number(props.lastRecordTime);
  const isStaleByLastRecord =
    Number.isFinite(lastRecordTime) && lastRecordTime > 0 && now - lastRecordTime > INACTIVE_TIME_SECONDS;
  const effectiveStatus = isStaleByLastRecord ? Status.Disconnected : status;

  const handleCardClick = () => {
    if (!isManageView) {
      navigate(`/data-explorer/${props.id}`);
      return;
    }

    const devicePath = (props.deviceType || '').startsWith('Controller') ? 'controller' : 'sensor';
    navigate(`/manage-device/${devicePath}/${props.id}`);
  };

  const areaName = props.areaName ?? props.area_name;
  const hasFloorNumber = props.floorNumber !== undefined && props.floorNumber !== null && props.floorNumber !== '';

  return (
    <Box
      className="main-card"
      onClick={handleCardClick}
      sx={{
        position: 'relative',
        // borderRadius: '8px 24px 24px 24px',
        width: { xs: '100%', sm: '19.5rem' },
        maxWidth: '19.5rem',
        aspectRatio: '579 / 387',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'transparent',
        overflow: 'visible',
        boxShadow: 'none',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: 'auto',
          aspectRatio: '576 / 383',
          zIndex: 0,
          pointerEvents: 'none',
          boxShadow: 'inset -12px -12px 18px rgba(0, 0, 0, 0.24)',
          WebkitMaskImage: `url(${emptySensorImage})`,
          maskImage: `url(${emptySensorImage})`,
          WebkitMaskPosition: 'top left',
          maskPosition: 'top left',
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskSize: '100% 100%',
          maskSize: '100% 100%',
        },

        '& img': {
          display: 'block',
          width: '100%',
          height: 'auto',
        },
        '&:hover': {
          cursor: 'pointer',
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out',
        },
        '&:hover .sensor-card-background': {
          filter: 'drop-shadow(0 6px 8px rgba(0, 0, 0, 0.16))',
        },
        transition: 'all 0.2s ease-in-out',
      }}>
      <Box
        component="img"
        className="sensor-card-background"
        src={emptySensorImage}
        alt=""
        aria-hidden="true"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: 'auto',
          objectPosition: 'center',
          pointerEvents: 'none',
          filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.28))',
          transition: 'filter 0.2s ease-in-out',
        }}
      />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ position: 'relative', zIndex: 1, padding: { xs: '10px 16px 0', sm: '10px 16px 0' } }}>
        <Box className="container" sx={{ width: '100%', minWidth: 0 }}>
          <Box
            className="topContainer"
            sx={{
              display: 'flex',
              width: '100%',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              pl: 0,
            }}>
            <Box sx={{ pt: 1, display: 'flex', alignItems: 'center', minWidth: 0, width: '100%' }}>
              <Paper
                className="sensorStatusIcon"
                color="white"
                elevation={0}
                // style={{ background: StatusColor[effectiveStatus][0] }}
                sx={{
                  // borderRadius: '4px 10px 10px 10px',
                  // padding: { xs: '4rem 3rem', sm: 1 },
                  // pl: { xs: 1, sm: 1 },
                  // pt: { xs: 7, sm: 1 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  // minWidth: { xs: '4rem', sm: '3rem' },
                  // width: { xs: '4rem', sm: '3rem' },
                  // height: { xs: '4rem', sm: '3rem' },
                  width: '77%',
                  flexShrink: 0,
                  backgroundColor: '#ffffff00',
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <SvgIcon
                    sx={{
                      color: StatusColor[effectiveStatus][0],
                      scale: { xs: 2, sm: 2 },
                      transformOrigin: 'left center',
                      position: 'relative',
                      left: '-4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    {StatusIcon[effectiveStatus]}
                  </SvgIcon>
                  <SmallStatusLabel statusLabel={effectiveStatus === Status.Disconnected ? 'OFFLINE' : 'ONLINE'} />
                </Box>
                <PingPongText
                  speed={4}
                  text={props.name || props.id}
                  sx={{
                    mt: 1.5,
                    fontSize: { xs: 16, sm: 16 },
                    fontWeight: 600,
                    color: '#000',
                    lineHeight: 1.25,
                    minWidth: 0,
                    // maxWidth: '92%',
                  }}
                />
              </Paper>
            </Box>
            {/* {!isManageView && effectiveStatus !== Status.Disconnected && (
              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: 20, sm: 20 },
                  right: { xs: 16, sm: 18 },
                  display: { xs: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 38, sm: 30 },
                  height: { xs: 38, sm: 30 },
                  pointerEvents: 'none',
                }}>
                <img src={ConnectionGif} alt="connected" style={{ width: '100%', height: 'auto' }} />
              </Box>
            )} */}
          </Box>
          <Box
            className="location-row"
            sx={{
              marginTop: { xs: 0.7, sm: 0.7 },
              width: '100%',
              paddingBottom: 1,
              '& .location-label': { display: 'none' },
              // '&:hover .location-label': { display: 'inline' },
            }}>
            <MDTypography
              fontSize={{ xs: 14, sm: 14 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontWeight: 'regular',
                color: '#000',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}>
              <ApartmentIcon sx={{ scale: 1.3, mr: 0.5, flexShrink: 0, color: '#000000b0' }} />
              <Box component="span" className="location-label">
                Building:{'\u00a0'}
              </Box>
              <Box component="span" className="location-value">
                {!props.building ? 'Unassigned' : props.building}
              </Box>
            </MDTypography>
            {(hasFloorNumber || areaName) && (
              <MDTypography
                fontSize={{ xs: 14, sm: 14 }}
                sx={{
                  display: 'block',
                  // pl: 3.4,
                  fontWeight: 'regular',
                  color: '#000',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}>
                {hasFloorNumber && (
                  <>
                    <Box component="span" className="location-label">
                      Floor:{'\u00a0'}
                    </Box>
                    <Box component="span" className="location-value">
                      {props.floorNumber}
                    </Box>
                  </>
                )}
                {hasFloorNumber && areaName && ' / '}
                {areaName && (
                  <>
                    <Box component="span" className="location-label">
                      Area:{'\u00a0'}
                    </Box>
                    <Box component="span" className="location-value">
                      {props.is_full_floor ? (isManageView ? 'Full floor' : 'unassigned') : areaName}
                    </Box>
                  </>
                )}
              </MDTypography>
            )}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          marginTop: 'auto',
          padding: { xs: '0 16px 12px', sm: '0 8px 10px' },
          gap: { xs: 0, sm: 0 },
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        {effectiveStatus !== Status.Disconnected &&
          Object.entries(props.params).map(
            ([key, value]) => value !== undefined && value !== null && <SensorParameterBox key={key} label={key} value={value} />,
          )}
      </Box>
    </Box>
  );
};

export default SensorCard;
